import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getCookie, setCookie } from 'cookies-next';
// Components
import Modal from '~src/components/Modal';
import { DashboardNodes } from './components';
import TwoFactorModal from './components/TwoFactorModal/TwoFactorModal';
import {
  MacOSIcon,
  VPSIcon,
  NodeEngineIcon,
} from '~src/components/Blockchain/NodeSetupIcons';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import Link from '~src/components/Link';
import NodeStatus from '~src/components/Blockchain/NodeStatus';
import {
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Button } from '~src/components/Button';
// Hooks
import { useAuth } from '~src/hooks/useAuth';
import { useWallet } from '~src/hooks/useWallet';
// Utils
import { useStoreContext } from '~src/providers/StoreProvider';
import { useCartContext } from '~src/providers/CartProvider';
import {
  createMemberPreference,
  getMemberPreferences,
} from '~src/resources/membership-service.resource';
import { brand } from '~src/brand/brand';
import { routes } from '~src/routes/routes';
import { config } from '~config';
import { DateTime } from 'luxon';

export default function Nodes() {
  const { fetcher } = useAuth();
  const router = useRouter();
  const { storeItems, storeError } = useStoreContext();
  const { cartError, cartItems } = useCartContext();
  const { walletCoins, walletCoinState } = useWallet();
  const [showSetupNode, setShowSetupNode] = useState(false);
  const [show2fa, setShow2fa] = useState(false);
  const [setupLinkUrl, setSetupLinkUrl] = useState(
    'https://give-dev-static-images.s3.us-west-2.amazonaws.com/nodes/give-vultr-dev.pdf'
  );
  // this macOS link is currently the same on dev and prod
  const [macOSLinkURL, setMacOSLinkURL] = useState(
    'https://give-static-images.s3.us-west-2.amazonaws.com/nodes/give-macos.pdf'
  );
  const [nodeInfoFetched, setNodeInfoFetched] = useState(false);
  const [loadingNodeInfo, setLoadingNodeInfo] = useState(false);
  const [smartNodeInfo, setSmartNodeInfo] = useState([]);
  const [liteNodeInfo, setLiteNodeInfo] = useState([]);
  const [licenseInfo, setLicenseInfo] = useState([]);
  const [licenseProductIds, setLicenseProductIds] = useState({ smart: 1, lite: 2 });
  const [purchasedNodes, setPurchasedNodes] = useState({ smart: 0, lite: 0 });
  const [smartCurrentPage, setSmartCurrentPage] = useState(1);
  const [liteCurrentPage, setLiteCurrentPage] = useState(1);
  const [products, setProducts] = useState(Object.values(storeItems));
  const [storeHasError, setStoreHasError] = useState(false);

  useEffect(() => {
    if (storeItems) {
      setProducts(Object.values(storeItems));
    } else if (storeError) {
      setStoreHasError(true);
    }
  }, [storeItems, storeError]);

  useEffect(() => {
    handleUserPreferences();
  }, []);

  useEffect(() => {
    if (window.location.hostname) {
      if (
        !window.location.hostname.includes('.dev.') &&
        !window.location.hostname.includes('localhost')
      ) {
        setSetupLinkUrl(
          'https://give-static-images.s3.us-west-2.amazonaws.com/nodes/give-vultr.pdf'
        );
        setMacOSLinkURL(
          'https://give-static-images.s3.us-west-2.amazonaws.com/nodes/give-macos.pdf'
        );
        setLicenseProductIds({ smart: 2, lite: 1 });
      }
    }
  }, []);

  useEffect(() => {
    async function check2fa() {
      return fetcher(getMemberPreferences())
        .then((preferences) => {
          const mfaPreference = preferences.find(
            (preference) => preference.name === 'mfa'
          );
          const isMfaDisabled =
            preferences.length === 0 ||
            mfaPreference === undefined ||
            mfaPreference?.value === 'false'; // backend returns a string boolean

          const isLoggingIn = !!router?.query?.login;
          isMfaDisabled && isLoggingIn && setShow2fa(true);
        })
        .catch(() => {
          // catch error and fail silently
        });
    }
    check2fa();
  }, []);

  useEffect(() => {
    const fetchPurchases = async () => {
      try {
        const purchaseData = await fetcher({
          method: 'GET',
          url: `${config.core.shoppingServiceUrl}/api/purchases`,
        });

        if (purchaseData.productQuantities) {
          let tmpData = { smart: 0, lite: 0 };
          for (let p of purchaseData.productQuantities) {
            if (p.productId === licenseProductIds.smart) {
              tmpData.smart = p.quantity;
            } else if (p.productId === licenseProductIds.lite) {
              tmpData.lite = p.quantity;
            }
          }

          setPurchasedNodes(tmpData);
        }
      } catch (err) {
        console.error('Failed to fetch purchase info: ', err);
      }
    };
    const fetchNodeInfo = async () => {
      setNodeInfoFetched(false);
      setLoadingNodeInfo(true);
      try {
        const nodeData = await fetcher({
          method: 'GET',
          url: `${config.backend.giveUrl}/nodes/info`,
        });
        setSmartNodeInfo(nodeData.smartNodes);
        setLiteNodeInfo(nodeData.liteNodes);
        setLicenseInfo(nodeData.licenses);
        setNodeInfoFetched(true);
      } catch (err) {
        console.error('Error getting node info:' + err);
      }
      setLoadingNodeInfo(false);
    };
    fetchNodeInfo();
    fetchPurchases();
  }, [fetcher, licenseProductIds.lite, licenseProductIds.smart]);

  const handleHide2fa = () => {
    setShow2fa(false);
    router.replace(router.pathname, { shallow: true });
  };

  const handleEnroll = () => {
    setShow2fa(false);
    router.replace(routes.manageAccountTwoFactorAuth.path);
  };

  //handle update of user preferences upon login
  const handleUserPreferences = useCallback(async () => {
    const localCookie = getCookie(brand.cookiesPreferencesKey);
    const localPref = localCookie && JSON.parse(localCookie);
    const storedPref = await fetcher(getMemberPreferences());

    if (!localPref && storedPref.length === 0) {
    } else if (localPref && storedPref.length === 0) {
      Object.entries(localPref).forEach(([key, value]) => {
        fetcher(createMemberPreference(key, value));
      });
    } else if (storedPref.length > 0) {
      //if the user has preferences in the db, it is to be used as the source of truth and should always update the local storage settings
      setCookie(
        brand.cookiesPreferencesKey,
        JSON.stringify({
          essential: true,
          analytics: storedPref.find((obj) => obj.name === 'analytics')?.value,
          functional: storedPref.find((obj) => obj.name === 'functional')?.value,
          marketing: storedPref.find((obj) => obj.name === 'marketing')?.value,
        }),
        { domain: brand.cookiesDomain }
      );
    }
  }, [fetcher]);

  const findRunTime = (date) => {
    var now = DateTime.now();
    var then = DateTime.fromISO(date);
    var diff = now.diff(then, ['days', 'hours']);
    return diff.toFormat("d' days', h 'hours'");
  };

  const smartNextPage = (e) => {
    e.preventDefault();

    if (numOfSmartPages > smartCurrentPage) {
      setSmartCurrentPage((prev) => prev + 1);
    }
  };

  const smartPreviousPage = (e) => {
    e.preventDefault();

    if (smartCurrentPage > 1) {
      setSmartCurrentPage((prev) => prev - 1);
    }
  };

  const liteNextPage = (e) => {
    e.preventDefault();

    if (numOfLitePages < liteCurrentPage) {
      setLiteCurrentPage((prev) => prev + 1);
    }
  };

  const litePreviousPage = (e) => {
    e.preventDefault();

    liteCurrentPage > 1 ? setLiteCurrentPage((prev) => prev - 1) : null;
  };

  // Pagination variables
  const pageSize = 5;
  const numOfSmartPages = Math.ceil(smartNodeInfo.length / pageSize);
  const numOfLitePages = Math.ceil(liteNodeInfo.length / pageSize);

  const smartStartIndex = (smartCurrentPage - 1) * pageSize;
  const smartEndIndex = smartStartIndex + pageSize;
  const visibleSmartNodes = smartNodeInfo.slice(smartStartIndex, smartEndIndex);

  const liteStartIndex = (liteCurrentPage - 1) * pageSize;
  const liteEndIndex = liteStartIndex + pageSize;
  const visibleLiteNodes = liteNodeInfo.slice(liteStartIndex, liteEndIndex);

  const smartLicenses = licenseInfo.filter((l) => l.licenseType === 'smart');
  const liteLicenses = licenseInfo.filter((l) => l.licenseType === 'lite');

  const pendingLicenses = { smart: 0, lite: 0 };
  if (nodeInfoFetched && smartLicenses.length < purchasedNodes.smart) {
    pendingLicenses.smart = purchasedNodes.smart - smartLicenses.length;
  }
  if (nodeInfoFetched && liteLicenses.length < purchasedNodes.lite) {
    pendingLicenses.lite = purchasedNodes.lite - liteLicenses.length;
  }

  return (
    <main>
      <div>
        {/*{(pendingLicenses.smart > 0 || pendingLicenses.lite > 0) && (
          <div className="rounded-md bg-yellow-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon
                  className="h-5 w-5 text-yellow-400"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Pending Licenses</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  {pendingLicenses.smart > 0 && (
                    <p>
                      You have {pendingLicenses.smart} pending smart license
                      {pendingLicenses.smart !== 1 ? 's' : ''}.
                    </p>
                  )}
                  {pendingLicenses.lite > 0 && (
                    <p>
                      You have {pendingLicenses.lite} pending lite license
                      {pendingLicenses.lite !== 1 ? 's' : ''}.
                    </p>
                  )}
                  <p>
                    These licenses should be granted shortly. You will receive a
                    confirmation email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}*/}
        <DashboardNodes
          walletItems={walletCoins}
          isLoading={walletCoinState.loading || loadingNodeInfo}
          smartNodes={smartNodeInfo}
          liteNodes={liteNodeInfo}
          licenses={licenseInfo}
          setShowSetupNode={setShowSetupNode}
          storeError={storeHasError}
          products={products}
          cartError={cartError.error}
          cartItems={cartItems}
        />
      </div>
      <div className="mt-6 flex flex-col -mx-4 sm:mx-0">
        <div className="inline-block min-w-full py-1 px-0.5 align-middle">
          <h1 className="text-xl font-semibold mb-2">Smart Nodes</h1>
          <div className="relative overflow-hidden shadow-md ring-1 ring-black ring-opacity-5 rounded-md md:rounded-lg">
            <table className="min-w-full table-fixed divide-y divide-gray-300">
              <thead className="bg-giveGray-50 border-solid border-b border-giveGray-200">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left align-middle text-xs sm:text-sm font-semibold text-giveGray-500"
                  >
                    HARDWARE ID
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left align-middle text-sm font-semibold text-giveGray-500 lg:table-cell"
                  >
                    RUN TIME
                  </th>
                  <th
                    scope="col"
                    className="pr-4 sm:px-3 py-3.5 text-left align-middle text-xs sm:text-sm font-semibold text-giveGray-500 table-cell"
                  >
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody className="divide-solid divide-y divide-gray-200 bg-white">
                {visibleSmartNodes.map((smartNode) => (
                  // {visibleMockNodes.map((smartNode) => (
                  <tr key={smartNode.node.id}>
                    <td className="w-1/4 whitespace-nowrap py-4 pl-4 pr-4 sm:pr-8 text-sm text-gray-900">
                      {smartNode.node.id}
                      <dl className="font-normal">
                        <dt className="sr-only">Run Time</dt>
                        <dd
                          className="mt-1 truncate text-gray-500 lg:hidden"
                          title={'Created: ' + smartNode.node.created}
                        >
                          Run Time: {findRunTime(smartNode.node.created)}
                        </dd>
                      </dl>
                    </td>
                    <td
                      className="w-1/4 hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell"
                      title={'Created: ' + smartNode.node.created}
                    >
                      {findRunTime(smartNode.node.created)}
                    </td>
                    <td className="w-1/5 whitespace-nowrap pr-4 sm:px-3 py-4 text-sm table-cell">
                      <NodeStatus
                        online={smartNode.online}
                        lastCheckIn={smartNode.latestMiningRecord?.lastCheckIn?.time}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-solid border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex justify-center items-center flex-1 sm:justify-between">
                <div className="hidden sm:flex">
                  <p className="text-sm text-gray-500">
                    Page
                    <span className="font-bold"> {smartCurrentPage} </span>
                    of
                    <span className="font-bold"> {numOfSmartPages} </span>
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                    <a
                      href="#"
                      className={
                        'relative inline-flex items-center rounded-l-md border border-solid border-gray-300 px-2 py-2 text-sm' +
                        (smartCurrentPage > 1
                          ? ' text-gray-600 hover:bg-gray-50 focus:z-20 font-bold'
                          : ' text-gray-300 cursor-default')
                      }
                      onClick={smartPreviousPage}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-7" />
                    </a>
                    <span className="relative z-10 inline-flex items-center bg-giveGray-100 px-4 py-2 text-sm text-giveGray-500 border-solid border border-giveGray-300 focus:z-20">
                      {smartCurrentPage}
                    </span>
                    <a
                      href="#"
                      className={
                        'relative inline-flex items-center rounded-r-md border border-solid border-gray-300 bg-white px-2 py-2 text-sm' +
                        (smartCurrentPage >= numOfSmartPages
                          ? ' text-gray-300 cursor-default'
                          : ' text-gray-600 hover:bg-gray-50 focus:z-20 font-bold')
                      }
                      onClick={smartNextPage}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-7" />
                    </a>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6 flex flex-col -mx-4 sm:mx-0">
        <div className="inline-block min-w-full py-1 px-0.5 align-middle">
          <h1 className="text-xl font-semibold mb-2">Lite Nodes</h1>
          <p className="text-sm font-light text-gray-500 mb-2">
            Every machine listed as &quot;online&quot; in the table below counts for up to
            100 active lite node licenses.
          </p>
          <div className="relative overflow-hidden shadow-md ring-1 ring-black ring-opacity-5 rounded-md md:rounded-lg">
            <table className="min-w-full table-fixed divide-y divide-gray-300">
              <thead className="bg-giveGray-50 border-solid border-b border-giveGray-200">
                <tr>
                  <th
                    scope="col"
                    className="py-3.5 pl-4 pr-3 text-left align-middle text-xs sm:text-sm font-semibold text-giveGray-500"
                  >
                    HARDWARE ID
                  </th>
                  <th
                    scope="col"
                    className="hidden px-3 py-3.5 text-left align-middle text-sm font-semibold text-giveGray-500 lg:table-cell"
                  >
                    RUN TIME
                  </th>
                  <th
                    scope="col"
                    className="pr-4 sm:px-3 py-3.5 text-left align-middle text-xs sm:text-sm font-semibold text-giveGray-500 table-cell"
                  >
                    STATUS
                  </th>
                </tr>
              </thead>
              <tbody className="divide-solid divide-y divide-gray-200 bg-white">
                {visibleLiteNodes.map((liteNode) => (
                  // {mockLiteNodeInfo.map((liteNode) => (
                  <tr key={liteNode.node.id}>
                    <td className="w-1/4 whitespace-nowrap py-4 pl-4 pr-4 sm:pr-8 text-sm text-gray-900">
                      {liteNode.node.id}
                      <dl className="font-normal">
                        <dt className="sr-only">Run Time</dt>
                        <dd className="mt-1 truncate text-gray-500 lg:hidden">
                          Run Time: {findRunTime(liteNode.node.created)}
                        </dd>
                      </dl>
                    </td>
                    <td className="w-1/4 hidden whitespace-nowrap px-3 py-4 text-sm text-gray-500 lg:table-cell">
                      {findRunTime(liteNode.node.created)}
                    </td>
                    <td className="w-1/5 whitespace-nowrap pr-4 sm:px-3 py-4 text-sm table-cell">
                      <NodeStatus online={liteNode.online} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex items-center justify-between border-t border-solid border-gray-200 bg-white px-4 py-3 sm:px-6">
              <div className="flex justify-center items-center flex-1 sm:justify-between">
                <div className="hidden sm:flex">
                  <p className="text-sm text-gray-500">
                    Page
                    <span className="font-bold"> {liteCurrentPage} </span>
                    of
                    <span className="font-bold"> {numOfLitePages}</span>
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                    <a
                      href="#"
                      className={
                        'relative inline-flex items-center rounded-l-md border border-solid border-gray-300 px-2 py-2 text-sm' +
                        (liteCurrentPage > 1
                          ? ' text-gray-600 hover:bg-gray-50 focus:z-20 font-bold'
                          : ' text-gray-300 cursor-default')
                      }
                      onClick={litePreviousPage}
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-7" />
                    </a>
                    <span className="relative z-10 inline-flex items-center bg-giveGray-100 px-4 py-2 text-sm text-giveGray-500 border-solid border border-giveGray-300 focus:z-20">
                      {liteCurrentPage}
                    </span>
                    <a
                      href="#"
                      className={
                        'relative inline-flex items-center rounded-r-md border border-solid border-gray-300 bg-white px-2 py-2 text-sm' +
                        (liteCurrentPage >= numOfLitePages
                          ? ' text-gray-300 cursor-default'
                          : ' text-gray-600 hover:bg-gray-50 focus:z-20 font-bold')
                      }
                      onClick={liteNextPage}
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-7" />
                    </a>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showSetupNode && (
        <Modal
          showClose={true}
          onClose={() => {
            setShowSetupNode(false);
          }}
          canCloseOnClickOut
          title={'Node Setup'}
        >
          <div className="rounded-md bg-yellow-50 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon
                  className="h-5 w-5 text-yellow-500"
                  aria-hidden="true"
                />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Attention</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    While registering your node,{' '}
                    <strong>if you have two-factor authentication (2FA) enabled</strong>,
                    you will need to{' '}
                    <Link className="underline" href="/settings/two-factor-auth">
                      temporarily disable it
                    </Link>{' '}
                    in order to register your node.{' '}
                    <strong>
                      Once the registration is complete, you can re-enable two-factor
                      authentication.
                    </strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col">
            <p className="text-left mb-8 px-2">
              Start running your node to support the GIVE Blockchain and earn digital
              rewards in exchange for securing the GIVE network.
            </p>
            <div className="flex flex-col md:flex-row justify-around items-center gap-4 md:gap-0">
              <div className="hidden md:flex flex-col w-44 items-center justify-center gap-2">
                <MacOSIcon width={100} height={100} />
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={macOSLinkURL}
                  className="border border-solid py-3 px-3 xl:px-5 text-sm rounded-lg font-semibold border-giveGray-300 mb-1 mt-2 hover:bg-giveGray-50"
                >
                  <ArrowTopRightOnSquareIcon className="h-6 w-6 inline relative -top-0.5 mr-1" />{' '}
                  <span className="inline">Instructions</span>
                </a>
                <span className="text-giveGray-500 ml-1 text-sm">
                  *Recommended for GIVE <strong>Lite</strong> Nodes
                </span>
              </div>
              <div className="flex flex-col w-44 items-center justify-center gap-2">
                <NodeEngineIcon />
                <a
                  href="https://app.nodeengine.io/?referralCode=Give-NE"
                  target="_blank"
                  rel="noreferrer"
                  className="border border-solid py-3 px-3 xl:px-5 text-sm rounded-lg font-semibold border-giveGray-300 mb-1 mt-2 hover:bg-giveGray-50"
                >
                  <ArrowTopRightOnSquareIcon className="h-6 w-6 inline relative -top-0.5 mr-1" />{' '}
                  Get Started
                </a>
                <span className="text-giveGray-500 ml-1 text-sm">
                  *Recommended for GIVE <strong>Smart</strong> and <strong>Lite</strong>{' '}
                  Nodes
                </span>
              </div>
              <div className="flex flex-col w-44 items-center justify-center gap-2">
                <VPSIcon width={100} height={100} />
                <a
                  target="_blank"
                  rel="noreferrer"
                  href={setupLinkUrl}
                  className="border border-solid py-3 px-3 xl:px-5 text-sm rounded-lg font-semibold border-giveGray-300 mb-1 mt-2 hover:bg-giveGray-50"
                >
                  <ArrowTopRightOnSquareIcon className="h-6 w-6 inline relative -top-0.5 mr-1" />{' '}
                  <span className="inline">Instructions</span>
                </a>
                <span className="text-giveGray-500 ml-1 text-sm">
                  *Recommended for GIVE <strong>Smart</strong> Nodes
                </span>
              </div>
            </div>
            <hr className="mt-5 mb-4 md:mb-6 -mx-6" />
            <div className="flex justify-end">
              <Button
                className={'mb-2 md:mb-4 mr-2 md:mr-1'}
                width={'w-32'}
                onClick={() => setShowSetupNode(false)}
              >
                Done
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {show2fa && (
        <TwoFactorModal handleCloseModal={handleHide2fa} handleContinue={handleEnroll} />
      )}
    </main>
  );
}

Nodes.protectedRoute = true;
